/**
 * Group flagged upper cabinets into contiguous under-cabinet LED strips.
 *
 * Pure. See `claude-docs/2026-09-24-under-cabinet-lighting-design.md`
 * ("Data", "Grouping into strips") for the spec this implements.
 *
 * Rotation convention (matches `shared-lib/geometry/objectFootprint.js` /
 * `shared-lib/collision/geometry.js`): `rotation.y` is radians, an object's
 * front normal (the direction its door faces, into the room) is
 * `(sin(rotation.y), cos(rotation.y))`, and its along-the-wall axis (the
 * direction `dimensions.x` runs) is `(cos(rotation.y), -sin(rotation.y))`.
 *
 * "Share rotation" is decided by comparing the two cabinets' FRONT NORMALS
 * directly (`cos(ry1 - ry2)` against a 1° cosine threshold), not by reducing
 * each yaw mod 180° first. Mod-180° would treat two cabinets whose yaws
 * differ by exactly π as "the same run" because they'd share an axis — but
 * an upper cabinet's yaw always encodes which way its door faces (into the
 * room), so a π-apart pair is facing OPPOSITE directions: back-to-back on
 * two different walls, never one physical run. Comparing normals via cosine
 * also sidesteps angle-wrap edge cases (e.g. -179° vs 179°) for free.
 */

const {
  isUpperCabinet,
  isOverFridgeCabinet,
  isCornerCabinet,
} = require('../kitchen-assembly/overFridgeCap');
const { objectsAreTouching } = require('../geometry/objectsTouch');
const { isRack } = require('../cabinet-runs/racks');

/** 2" behind the door face, in meters — where the strip line sits. */
const STRIP_FRONT_INSET_M = 0.0508;
/** 1" short of each end of the run, in meters — the LED channel's end caps. */
const STRIP_END_INSET_M = 0.0254;

const TOUCH_TOLERANCE_M = 0.01;
const UNDERSIDE_TOLERANCE_M = 0.01;
const ROTATION_TOLERANCE_RAD = (1 * Math.PI) / 180;
const ROTATION_COS_THRESHOLD = Math.cos(ROTATION_TOLERANCE_RAD);

function isFiniteNumber(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function hasUsableGeometry(o) {
  const c = o?.center;
  const d = o?.dimensions;
  if (!c || !d) return false;
  return (
    isFiniteNumber(c.x) &&
    isFiniteNumber(c.y) &&
    isFiniteNumber(c.z) &&
    isFiniteNumber(d.x) &&
    d.x > 0 &&
    isFiniteNumber(d.y) &&
    d.y > 0 &&
    isFiniteNumber(d.z) &&
    d.z > 0
  );
}

/**
 * Can this object carry an under-cabinet light at all — THE rule, whatever the
 * flag says. An upper cabinet that is neither the over-fridge unit (the fridge
 * is under it, not a counter) nor a corner unit (v1: its renderer draws no
 * channel and its L footprint is not one straight run).
 *
 * Exported because the panel offers the toggle, "apply to all uppers in this
 * room" picks its targets, and a run re-solve re-stamps its uppers by this
 * same predicate — a second copy anywhere would let the panel offer a light
 * the strips then refuse to draw.
 */
function isUnderCabinetLightEligible(o) {
  if (!o) return false;
  if (!isUpperCabinet(o)) return false;
  if (isOverFridgeCabinet(o)) return false;
  if (isCornerCabinet(o)) return false;
  // A stacked upper stands ON another upper: its underside is a cabinet top,
  // not the counter's ceiling (plan 2026-10-21, C21).
  if (o.category?.stack) return false;
  // floating shelves have no underside to channel a strip into, and an end
  // shelf's curves round the run's end
  if (o.category?.floatingShelves || o.category?.endShelf) return false;
  // a microwave shelf's underside holds the microwave's shelf, not a strip
  if (o.category?.microwaveShelf) return false;
  // a wall cabinet with drawers stands on the counter
  if (o.category?.wallDrawers) return false;
  // a valance bay is a board across an open gap, no cabinet
  if (o.category?.valanceBay) return false;
  // a rack is open, hung high (`racks.js`)
  if (isRack(o)) return false;
  // an angled end's underside is cut on the diagonal (`angledEnds.js`)
  if (o.category?.angledEnd) return false;
  // the cabinet stacked over a rack stands on the rack (`rackStacks.js`)
  if (o.category?.rackCompanion === 'above') return false;
  return true;
}

/**
 * Flagged, eligible upper cabinets with a usable center + dimensions.
 * Everything else (unflagged, lower cabinets, over-fridge, corner units,
 * malformed) is silently excluded — never throws.
 */
function isStripCandidate(o) {
  if (!isUnderCabinetLightEligible(o)) return false;
  if (o.underCabinetLight !== true) return false;
  return hasUsableGeometry(o);
}

function rotationOf(o) {
  return o.rotation?.y ?? 0;
}

function underside(o) {
  return o.center.y - o.dimensions.y / 2;
}

function frontNormalsAgree(ryA, ryB) {
  return Math.cos(ryA - ryB) >= ROTATION_COS_THRESHOLD;
}

function undersidesAgree(a, b) {
  return Math.abs(underside(a) - underside(b)) <= UNDERSIDE_TOLERANCE_M;
}

/** Whether two candidates belong to the same physical strip run. */
function canJoinStrip(a, b) {
  const ryA = rotationOf(a);
  const ryB = rotationOf(b);
  if (!frontNormalsAgree(ryA, ryB)) return false;
  if (!undersidesAgree(a, b)) return false;
  return objectsAreTouching(a, b, TOUCH_TOLERANCE_M);
}

/** Minimal union-find over `[0, count)`. */
function createUnionFind(count) {
  const parent = Array.from({ length: count }, (_, i) => i);
  function find(i) {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  }
  function union(i, j) {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[ri] = rj;
  }
  return { find, union };
}

/** Partition `candidates` into connected components under `canJoinStrip`. */
function groupIntoComponents(candidates) {
  const { find, union } = createUnionFind(candidates.length);
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      if (canJoinStrip(candidates[i], candidates[j])) union(i, j);
    }
  }
  const byRoot = new Map();
  for (let i = 0; i < candidates.length; i++) {
    const root = find(i);
    if (!byRoot.has(root)) byRoot.set(root, []);
    byRoot.get(root).push(candidates[i]);
  }
  return [...byRoot.values()];
}

/** Circular mean of a set of angles (radians) — order-independent. */
function circularMean(angles) {
  let sumSin = 0;
  let sumCos = 0;
  for (const a of angles) {
    sumSin += Math.sin(a);
    sumCos += Math.cos(a);
  }
  return Math.atan2(sumSin, sumCos);
}

/**
 * Build the Strip for one connected component of candidates, or `null` when
 * the resulting run is too short to hold a real LED channel (span at or
 * below the two end insets). A degenerate strip must never be returned:
 * downstream it becomes a zero- (or negative-) width `RectAreaLight`, which
 * is not "a very small light" but a light with no emitting area at all.
 *
 * The strip's own axis/normal come from the component's circular-mean
 * rotation (members are within 1° of each other by construction, so this is
 * a stable, order-independent reference frame). Each member's own footprint
 * is still measured with ITS OWN rotation before being projected onto that
 * shared frame, so a member that is (within tolerance) not exactly aligned
 * with the mean still contributes its true extent.
 */
function buildStrip(members) {
  const sorted = [...members].sort((a, b) =>
    String(a.id).localeCompare(String(b.id))
  );
  const cabinetIds = sorted.map((o) => String(o.id));
  const id = cabinetIds.join('+');

  const rotationY = circularMean(sorted.map(rotationOf));
  const axisDir = { x: Math.cos(rotationY), z: -Math.sin(rotationY) };
  const normalDir = { x: Math.sin(rotationY), z: Math.cos(rotationY) };

  let minT = Infinity;
  let maxT = -Infinity;
  let sumCross = 0;
  let sumY = 0;

  for (const o of sorted) {
    const ry = rotationOf(o);
    const ownAxisDir = { x: Math.cos(ry), z: -Math.sin(ry) };
    const ownNormalDir = { x: Math.sin(ry), z: Math.cos(ry) };
    const halfWidth = o.dimensions.x / 2;
    const halfDepth = o.dimensions.z / 2;
    const cx = o.center.x;
    const cz = o.center.z;

    // The cabinet's own along-axis extremes, projected onto the strip's
    // shared axis to accumulate the run's span.
    const p1x = cx - halfWidth * ownAxisDir.x;
    const p1z = cz - halfWidth * ownAxisDir.z;
    const p2x = cx + halfWidth * ownAxisDir.x;
    const p2z = cz + halfWidth * ownAxisDir.z;
    const t1 = p1x * axisDir.x + p1z * axisDir.z;
    const t2 = p2x * axisDir.x + p2z * axisDir.z;
    minT = Math.min(minT, t1, t2);
    maxT = Math.max(maxT, t1, t2);

    // Door face, then inset back toward the wall to the strip line.
    const frontFaceX = cx + halfDepth * ownNormalDir.x;
    const frontFaceZ = cz + halfDepth * ownNormalDir.z;
    const anchorX = frontFaceX - STRIP_FRONT_INSET_M * ownNormalDir.x;
    const anchorZ = frontFaceZ - STRIP_FRONT_INSET_M * ownNormalDir.z;
    sumCross += anchorX * normalDir.x + anchorZ * normalDir.z;

    sumY += underside(o);
  }

  const span = maxT - minT;
  const length = span - 2 * STRIP_END_INSET_M;
  if (!(length > 0)) return null;
  const midT = (minT + maxT) / 2;
  const cross = sumCross / sorted.length;
  const y = sumY / sorted.length;

  return {
    id,
    cabinetIds,
    center: {
      x: midT * axisDir.x + cross * normalDir.x,
      y,
      z: midT * axisDir.z + cross * normalDir.z,
    },
    rotationY,
    length,
    frontInset: STRIP_FRONT_INSET_M,
  };
}

/**
 * @param {Array} roomObjects - A room's `objects` array (or any superset).
 * @returns {Array} Strips, sorted by `id` — deterministic regardless of
 *   input order.
 */
function underCabinetStrips(roomObjects) {
  const candidates = (roomObjects || []).filter(isStripCandidate);
  const components = groupIntoComponents(candidates);
  return components
    .map(buildStrip)
    .filter(Boolean)
    .sort((a, b) => a.id.localeCompare(b.id));
}

/**
 * How much under-cabinet light a room's design runs, metres: the strips'
 * own lengths (`underCabinetStrips`, each a run's span less its end caps),
 * summed — what the estimate bills its install by
 * (`install-under-cabinet-light`). 0 for none.
 */
function underCabinetLightLengthM(roomObjects) {
  return underCabinetStrips(roomObjects).reduce((m, s) => m + s.length, 0);
}

module.exports = {
  underCabinetStrips,
  underCabinetLightLengthM,
  isUnderCabinetLightEligible,
  STRIP_FRONT_INSET_M,
  STRIP_END_INSET_M,
};
